import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';

import { isDefined } from '@/components/WellLoggingOperations/utils';
import { WidthType } from '@/components/WellLoggingOperations/types';

import { TABLET_SETTINGS_KEYS } from '@/store/well-logs/tabletSettings';
import type {
    CurveSettings,
    GlobalCurvesSettings,
    ResearchesByDate,
    RootDispatch,
    RootState,
    TabletColumns,
    TabletSettings,
    Template,
} from '@/components/WellLoggingOperations/types';

import {
    defaultColumnsVisibility,
    defaultColumnsSizing,
    paramColumnToColumnKey,
    defaultColumnsOrder,
} from '@/store/well-logs/tabletSettings/config';
import { restoreCurveSettingsObj } from '@/store/well-logs/profiles/libs/prepareProfileWellColumns';
import { getWellLoggingKeyForTemplate } from '@/store/well-logs/profiles/libs/getWellLoggingKeysFromWellProfile';
import { updateDataSelection } from '../../TabletSettings/Researches/helpers';
import { isValidJSON } from '../../Toolbar/modals/Templates/ui/Info/Info';
import { getItemByKeyRecursive } from '@/components/Lists/lib';
import { NODE_TYPE } from '@/lib/nodeType';

const defaultWellLoggingTypes = ['IK', 'PS', 'BK3'];

/*
    Мы можем из шаблона достать все поля, кроме wellLoggingKeys
    потому что они зависят от исследований
*/
type SettingsFromTemplate = Omit<TabletSettings, 'isKeysInit' | 'rigisColumnsOrder' | 'rigisColumnsCount'> & {
    typeSettings: Record<string, CurveSettings>;
};

const prepareCurveSettings = (globalCurvesSettings: GlobalCurvesSettings) => {
    const TYPES = [
        ['M2A0.5B', 'M2A05B'],
        ['A2M0.5N', 'A2M05N'],
        ['M2N0.5B', 'M2N05B'],
    ];
    const extendedSettings = { ...globalCurvesSettings };
    for (const type of TYPES) {
        if (globalCurvesSettings[type[0]]) {
            extendedSettings[type[1]] = globalCurvesSettings[type[0]];
        }
    }
    for(const type of Object.keys(extendedSettings)){
        if(extendedSettings[type].minMaxValues){
            extendedSettings[type].isChangeMinMaxValues = true;
        }
    }

    return extendedSettings;
};

export const useCurrentTemplateCurves = (wellKey: string) => {
    const templates = useSelector((state: RootState) => state.templates.data);
    const defaultTemplateId = useSelector((state: RootState) => state.templates.defaultTemplateId);
    const currentTemplateId = useSelector((state: RootState) => state.templateWells.currentWellsTemplateId[wellKey]);
    const templateId = currentTemplateId || defaultTemplateId;

    const template = templateId ? templates.byId[templateId] : null;

    const carrotageNotParsed = template?.params.filter(param => param.paramColumn === 7);

    const carrotageParsed = carrotageNotParsed?.map(param => {
        let newChildren;
        if (param.children) {
            newChildren = param.children.map(child => {
                let strParamValueObj = null;
                if (
                    child?.strParamValue?.includes('curveSettings') ||
                    child?.strParamValue?.includes('curveDisplaySettings')
                ) {
                    strParamValueObj = restoreCurveSettingsObj(child.strParamValue);
                }
                return {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    strParamValue: strParamValueObj
                        ? //@ts-ignore
                          strParamValueObj.curveSettings.title
                        : child.strParamValue,
                    param: child?.paramNumber,
                    curveSettings: strParamValueObj?.curveSettings,
                    curveDisplaySettings: strParamValueObj?.curveDisplaySettings,
                };
            });
        }
        return { ...param, children: newChildren };
    });

    let wellLoggingTypes: string[] = [];

    if (carrotageParsed && carrotageParsed[0].children) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        wellLoggingTypes = carrotageParsed[0].children.map(({ strParamValue }) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
            return isValidJSON(strParamValue) ? (JSON.parse(strParamValue).curveSettings[2] ?? '') : strParamValue;
        });
    }

    return wellLoggingTypes;
};

const getColumnsOrder = (templateOrder: TabletColumns[] | undefined): TabletColumns[] => {
    if (templateOrder) {
        const restColumns = defaultColumnsOrder.filter(key => !templateOrder.find(tKey => tKey === key));
        return [...templateOrder, ...restColumns];
    }
    return defaultColumnsOrder;
};

const getSettingsFromTemplate = (
    template: Template | null | undefined,
    isFgiAllowed: boolean,
    researches: ResearchesByDate[],
    dateCurveKey: 'is_first_date_curve' | 'is_last_date_curve',
): SettingsFromTemplate => {
    const visibility = { ...defaultColumnsVisibility };
    const sizes = { ...defaultColumnsSizing };
    const rigisParamsHistogramIds: number[] = [];
    const fgiParamsHistogramIds: number[] = [];

    if (isDefined(template)) {
        (Object.keys(visibility) as TabletColumns[]).forEach(key => {
            visibility[key] = false;
        });

        // parse template
        template.params.forEach(param => {
            const columnKey = paramColumnToColumnKey[param.paramColumn];

            if (columnKey) {
                if (!isFgiAllowed) {
                    if (columnKey === 'thicknessInterlayer') {
                        visibility[columnKey] = false;
                    }
                } else {
                    visibility[columnKey] = true;
                }
                sizes[columnKey] = {
                    ...sizes[columnKey],
                    width: Math.max(sizes[columnKey]?.minWidth, param?.paramWidth),
                    widthType: WidthType.Pixel,
                };
                if (columnKey === 'paramsHistogram' && isFgiAllowed) {
                    for (const p of param?.children ?? []) {
                        if (p.paramNumber) {
                            fgiParamsHistogramIds.push(p.paramNumber);
                        }
                    }
                }
                if (columnKey === 'paramsHistogram' && !isFgiAllowed) {
                    for (const p of param?.children ?? []) {
                        if (p.paramNumber) {
                            fgiParamsHistogramIds.push(p.paramNumber);
                        }
                    }
                }
            }
        });
    }

    if (
        (visibility.geoObjects || visibility.lithology || visibility.thicknessInterlayer) &&
        !visibility.allGeoObjects
    ) {
        visibility.allGeoObjects = true;
        if (visibility.geoObjects) {
            visibility.lithology = true;
        }
    }

    const otherParams = template?.params.filter(param => param.paramColumn !== 7);
    const carrotageNotParsed = template?.params.filter(param => param.paramColumn === 7);

    const carrotageParsed = carrotageNotParsed?.map(param => {
        let newChildren;
        if (param.children) {
            newChildren = param.children.map(child => {
                let strParamValueObj = null;
                if (
                    child?.strParamValue?.includes('curveSettings') ||
                    child?.strParamValue?.includes('curveDisplaySettings')
                ) {
                    strParamValueObj = restoreCurveSettingsObj(child.strParamValue);
                }
                return {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    strParamValue: strParamValueObj
                        ? //@ts-ignore
                          strParamValueObj.curveSettings.title
                        : child.strParamValue,
                    param: child?.paramNumber,
                    curveSettings: strParamValueObj?.curveSettings,
                    curveDisplaySettings: strParamValueObj?.curveDisplaySettings,
                };
            });
        }
        return { ...param, children: newChildren };
    });

    const newParams = [...(otherParams ?? []), ...(carrotageParsed ?? [])];

    let wellLoggingKeys: string[] = [];
    let curveSettings = {};
    let curveDisplaySettings = {};
    let typeSettings = {};

    if (researches && !isEmpty(researches)) {
        const wellLoggingKeysAndCurvesFromStateWell = getWellLoggingKeyForTemplate(
            updateDataSelection(researches, null, dateCurveKey, 'is_first_date_curve_by_interval'),
            { params: newParams },
            template?.researchOrder,
        );
        wellLoggingKeys = (wellLoggingKeysAndCurvesFromStateWell?.wellLoggingKeys as string[]) ?? [];
        curveSettings = wellLoggingKeysAndCurvesFromStateWell?.curveSettings ?? {};
        curveDisplaySettings = wellLoggingKeysAndCurvesFromStateWell?.curveDisplaySettings ?? {};
        typeSettings = wellLoggingKeysAndCurvesFromStateWell?.typeSettings ?? {};
    }

    return {
        columnsVisibility: template?.columnsVisibility ?? visibility,
        columnsSizing: sizes,
        columnsOrder: getColumnsOrder(template?.columnsOrder),
        headerSizes: {
            height: 143,
            maxHeight: 600,
            minHeight: 0,
            rigisColumnHeight: 100,
        },
        prevTemplateId: template?.id ?? null,
        fgiParamsHistogramIds: fgiParamsHistogramIds,
        rigisParamsHistogramIds: rigisParamsHistogramIds,
        coreKeys: [],
        wellLoggingKeys,
        curveSettings,
        curveDisplaySettings,
        typeSettings,
    };
};

const getWellLoggingKeys = (
    researches: ResearchesByDate[],
    template?: Template | null,
    isFirstDateCurve?: boolean,
    isLastDateCurve?: boolean,
): TabletSettings['wellLoggingKeys'] => {
    let wellLoggingTypes = defaultWellLoggingTypes;

    const params = template?.params.find(p => paramColumnToColumnKey[p.paramColumn] === 'carrotage');

    if (params?.children) {
        wellLoggingTypes = params.children.map(x => x.strParamValue);
    }

    const wellLoggingKeys: string[] = [];

    researches.forEach(r1 => {
        r1.children.forEach(r2 => {
            r2.children.forEach(research => {
                if (isFirstDateCurve && research.is_first_date_curve) {
                    wellLoggingKeys.push(research.key);
                } else if (isLastDateCurve && research.is_last_date_curve) {
                    wellLoggingKeys.push(research.key);
                } else if (!isFirstDateCurve && !isLastDateCurve && wellLoggingTypes.includes(research.type)) {
                    wellLoggingKeys.push(research.key);
                }
            });
        });
    });

    return wellLoggingKeys;
};

/**
 * Mapping active template to tablet settings
 */
export const useCachedTabletSettings = () => {
    const tabletSettings = useSelector((state: RootState) => state.tabletSettings);

    // cached to localStorage;
    useEffect(() => {
        try {
            localStorage.setItem(TABLET_SETTINGS_KEYS, JSON.stringify(tabletSettings));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('error:tablet_settings_set_to_local_storage', error);
        }
    }, [tabletSettings]);
};

export const useSettings = (wellId: string, wellKey: string) => {
    /*
        Получаем все доступные шаблоны
    */
    const templates = useSelector((state: RootState) => state.templates.data);
    /*
        Получаем шаблон по умолчанию
    */
    const defaultTemplateId = useSelector((state: RootState) => state.templates.defaultTemplateId);
    /*
        Получаем шаблон, который был применен к выбранной скважине
    */
    const currentTemplateId = useSelector((state: RootState) => state.templateWells.currentWellsTemplateId[wellKey]);

    /*
        Получаем исследования для конкретного планшета по его Id
    */
    const researches = useSelector((state: RootState) => state.researches.data[wellId]);

    const intervalResearches = useSelector((state: RootState) => state.intervalResearches.data[wellId]);
    /*
        Получаем настройки скважины по ключу
    */
    const tabletSettings = useSelector((state: RootState) => state.tabletSettings[wellKey]);
    /*
        Получаем ШАБЛОН по последнему примененному ID шаблон
    */
    const templateId = currentTemplateId || defaultTemplateId;

    const isFgiAllowed = useSelector((state: RootState) => state.tablets.isFgiAllowed);

    const { isLastDateCurve } = useSelector((state: RootState) => state.displaySettings);
    const { forceUpdateTablet } = useSelector((state: RootState) => state.displaySettings);

    const template = templateId ? templates.byId[templateId] : null;

    /*
        Старое правило, как раньше как то работало
        const isNotInit = !tabletSettings;
    */

    const isTemplateChange = tabletSettings?.prevTemplateId !== templateId;

    const dispatch = useDispatch<RootDispatch>();
    const { setTabletSettings } = dispatch.tabletSettings;
    const { applyTooltipSettings } = dispatch.columnsTooltipProps;
    const { updateDisplaySettings, setForceUpdateState } = dispatch.displaySettings;
    const { setCurvesSettings } = dispatch.templates;

    const wellLoggingKeys = useMemo(() => getWellLoggingKeys(researches ?? [], template), [researches, template]);
    const selectedDefaultCurves = useMemo(() => {
        if (!isEmpty(intervalResearches)) {
            const ids: string[] = [];

            intervalResearches.forEach(r1 => {
                r1.children.forEach(r2 => {
                    r2.children.forEach(intervalResearch => {
                        ids.push(intervalResearch.key);
                    });
                });
            });

            return ids;
        }
        return [];
    }, [intervalResearches]);
    /*
        Обновляем только если:
        - загрузились исследования (wellLoggingKeys.length > 0) AND
        - изменился шаблон OR
        - не было инициализации tabletSettings.isKeysInit OR
        - список кривых, выбранных через отчет "Исследование скважин" не пустой
    */

    const activeItemKey = useSelector<RootState>(state => state.lists.activeItemKey);
    const lists = useSelector<RootState>(state => state.lists.data);
    const { activeGroupName } = useSelector((state: RootState) => state.tablets);

    const activeItem = getItemByKeyRecursive(lists, activeItemKey);
    const isProfile =
        !activeItem?.type ||
        (activeItem?.type !== NODE_TYPE.PROFILE && activeItem?.id) ||
        //@ts-ignore
        lists.filter(list => list.key === activeGroupName)[0]?.type !== 'profile';

    const isFirstLoad = tabletSettings?.isFirstLoad;

    useEffect(() => {
        if ((isTemplateChange || forceUpdateTablet) && !(isFirstLoad && isProfile)) {
            const { typeSettings, ...data } = getSettingsFromTemplate(
                template,
                isFgiAllowed,
                researches,
                isLastDateCurve ? 'is_last_date_curve' : 'is_first_date_curve',
            );

            const displaySettings: {
                globalCurvesSettings?: GlobalCurvesSettings;
                emptyColumnsDataSettings?: boolean;
            } = {};

            if (template?.globalCurvesSettings) {
                displaySettings.globalCurvesSettings = prepareCurveSettings(template.globalCurvesSettings);
            }

            displaySettings.emptyColumnsDataSettings = template?.emptyColumnsDataSettings;

            if (template?.tooltipSettings) {
                applyTooltipSettings(template.tooltipSettings);
            }

            if (templateId) {
                setCurvesSettings(typeSettings);
            }
            setTabletSettings({ wellKey, data });
            updateDisplaySettings(displaySettings);
            setForceUpdateState(false);
        } else if ((wellLoggingKeys.length && !tabletSettings?.isKeysInit) || intervalResearches?.length) {
            setTabletSettings({
                wellKey,
                data: {
                    wellLoggingKeys: [...wellLoggingKeys, ...selectedDefaultCurves],
                    isKeysInit: true,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        intervalResearches,
        selectedDefaultCurves,
        setTabletSettings,
        tabletSettings?.isKeysInit,
        wellKey,
        wellLoggingKeys,
        isTemplateChange,
        template,
        isLastDateCurve,
        forceUpdateTablet,
    ]);

    // cached to localStorage;
    useCachedTabletSettings();
};
